import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { StepTitle } from '../step-title';
import { NumberedStep } from '../numbered-step';

export const TripodSetupStep: React.FC = () => {
  return (
    <Flex
      direction="column"
      align="center"
      minH="100vh"
      px={6}
      pt={16}
      pb={24}
      bg="white"
      overflowY="auto"
    >
      <VStack gap={5} maxW="500px" w="full">
        <StepTitle stepNumber={3} title="Set Up Tripod And Mount Camera" />

        {/* Tripod Setup */}
        <Box
          w="full"
          p={5}
          bg="gray.50"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
        >
          <VStack align="flex-start" gap={2}>
            <NumberedStep number={1}>
              Loosen top and bottom screws of your tripod
            </NumberedStep>
            <NumberedStep number={2}>
              Extend out the legs
            </NumberedStep>
            <NumberedStep number={3}>
              Level the tripod using leg clips
            </NumberedStep>
            <NumberedStep number={4}>
              Secure tripod with sandbags
            </NumberedStep>
          </VStack>
        </Box>

        {/* Tripod Diagram */}
        <Box w="full" borderRadius="xl" overflow="hidden">
          <img
            src={`${import.meta.env.BASE_URL}images/tripod-setup.webp`}
            alt="Tripod setup instructions"
            style={{ width: '100%', height: 'auto' }}
          />
        </Box>

        {/* Sandbag Warning */}
        <Flex
          align="flex-start"
          gap={3}
          w="full"
          p={4}
          bg="yellow.50"
          borderRadius="xl"
          border="1px solid"
          borderColor="yellow.400"
        >
          <Flex
            align="center"
            justify="center"
            w="24px"
            h="24px"
            minW="24px"
            borderRadius="full"
            bg="yellow.500"
            color="white"
            fontSize="sm"
            fontWeight="bold"
          >
            !
          </Flex>
          <Text fontSize="sm" color="gray.700" lineHeight="1.5">
            <Text as="span" fontWeight="bold">
              Secure the tripod with the included sandbags.
            </Text>{' '}
            Failure to do so may increase the chance of the tripod tipping over,
            causing injury or equipment damage.
          </Text>
        </Flex>

        {/* Camera Mount */}
        <Box
          w="full"
          p={5}
          bg="gray.50"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
        >
          <VStack align="flex-start" gap={2}>
            <NumberedStep number={5}>
              Connect the HDMI cable to the camera. Be careful not to push the
              tilt head manually while connecting the HDMI
            </NumberedStep>
            <NumberedStep number={6}>
              Screw the (black) camera mount into the camera. This step may have
              already been completed. If so, skip and leave it attached when
              packing down the kit
            </NumberedStep>
            <NumberedStep number={7}>
              Insert the camera into the top mount of the tripod
            </NumberedStep>
          </VStack>
        </Box>

        {/* Camera Diagram */}
        <Box w="full" borderRadius="xl" overflow="hidden">
          <img
            src={`${import.meta.env.BASE_URL}images/tripod-camera.webp`}
            alt="Camera mounting instructions"
            style={{ width: '100%', height: 'auto' }}
          />
        </Box>

        {/* Step 8 + Clip Diagram */}
        <Box
          w="full"
          p={5}
          bg="gray.50"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
        >
          <NumberedStep number={8}>
            Close the clip to secure the camera
          </NumberedStep>
        </Box>

        <Box w="full" maxW="300px" borderRadius="xl" overflow="hidden">
          <img
            src={`${import.meta.env.BASE_URL}images/close-tripod-clip.webp`}
            alt="Close the tripod clip to secure the camera"
            style={{ width: '100%', height: 'auto' }}
          />
        </Box>

        {/* Step 9 + Camera On Diagram */}
        <Box
          w="full"
          p={5}
          bg="gray.50"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
        >
          <NumberedStep number={9}>
            Press the black button to turn the camera on (solid white light =
            camera on).
          </NumberedStep>
        </Box>

        <Box w="full" maxW="250px" borderRadius="xl" overflow="hidden">
          <img
            src={`${import.meta.env.BASE_URL}images/camera-on-indicator.webp`}
            alt="Solid white light indicates camera is on"
            style={{ width: '100%', height: 'auto' }}
          />
        </Box>

        {/* Tilt Head Warning */}
        <Flex
          align="flex-start"
          gap={3}
          w="full"
          p={4}
          bg="red.50"
          borderRadius="xl"
          border="1px solid"
          borderColor="red.400"
        >
          <Flex
            align="center"
            justify="center"
            w="24px"
            h="24px"
            minW="24px"
            borderRadius="full"
            bg="red.500"
            color="white"
            fontSize="sm"
            fontWeight="bold"
          >
            ✕
          </Flex>
          <Text fontSize="sm" color="gray.700" lineHeight="1.5">
            <Text as="span" fontWeight="bold">
              Do not press or push the camera manually.
            </Text>{' '}
            This may damage the tilt head. The camera tilt head is only to be
            operated via FrogBox Connect app.
          </Text>
        </Flex>
      </VStack>
    </Flex>
  );
};
